import styles from './SelectedDay.module.scss'
import { Button, ButtonGroup, Paragraph } from '~elements'
import { DATE_FORMAT, DATE_FORMATTER } from '~constants'
import dayjs from 'utils/dayjs'
import { useEffect, useMemo, useState } from 'react'
import { SelectedDayProps } from 'types/calendar'
import { FormattedEvent } from 'components/modules/Calendar/types/Event'
import { getCtaData, getEverySphereFlagDescription } from 'components/modules/Calendar/utils'
import { CtaData } from 'components/modules/Calendar/types'
import { isExternalLink } from 'utils'
import { useWindowSize } from '~utils/hooks'
import { ButtonProps } from '~types'

const SelectedDay = ({
  activeShowId,
  disclaimer,
  eventInfo,
  groupTicketsOfferText,
  onTimeSelect
}: SelectedDayProps) => {
  const { isBreakpoint: isBelowMd } = useWindowSize('md', '<')
  // Extract artist and filter events for the specific artist
  const artist = eventInfo?.artistDetail
  const events = useMemo(
    () => (eventInfo?.events ?? []).filter(e => e.artistId === artist?.artistId),
    [eventInfo?.events, artist?.artistId]
  )

  // Format date strings for display
  const currentEventDate = events[0] ? dayjs(events[0].dateTime, DATE_FORMAT.DATE_TIME) : null
  const formattedDate = currentEventDate?.format(DATE_FORMATTER.SELECTED_DATE) ?? ''
  const formattedDay = currentEventDate?.format(DATE_FORMATTER.SELECTED_DAY) ?? ''
  const activeFormattedDate = currentEventDate?.format('MM/DD/YYYY') ?? ''
  const linkModule = 'calendar_modal'
  const [activeEvent, setActiveEvent] = useState<null | FormattedEvent>(null)
  const [ctaData, setCtaData] = useState<CtaData>({ title: null, url: null })

  // Get sphere flag descriptions for the events
  const flags = getEverySphereFlagDescription(events)

  useEffect(() => {
    if (events.length > 0 && activeShowId) {
      const foundEvent = events.find(e => e.id === activeShowId) ?? null
      setActiveEvent(foundEvent)
      const ctaData = getCtaData(foundEvent, artist)
      setCtaData(ctaData)
    } else {
      setCtaData(getCtaData(null, artist))
    }
  }, [events, activeShowId, artist])

  const buttonData = [
    {
      title: ctaData.title ?? '',
      isTargetBlank: true,
      disabled: events.length === 0,
      color: 'primary',
      fill: 'solid',
      size: 'lg',
      url: ctaData.url,
      analytics: {
        eventData: {
          ticketmasterEventId: activeEvent?.id || '',
          eventDate: activeFormattedDate
        }
      }
    },
    {
      title: artist?.ctaSecondaryTitle,
      isTargetBlank: isExternalLink(artist?.ctaSecondaryUrl || ''),
      color: 'gold',
      fill: 'solid',
      size: 'lg',
      url: artist?.ctaSecondaryUrl,
      analytics: {
        eventData: {
          ticketmasterEventId: activeEvent?.id || '',
          eventDate: activeFormattedDate
        }
      }
    }
  ]

  return (
    <div className={`${styles['selected-event']}`}>
      <div className={styles['selected-event__date-times-container']}>
        <div className={styles['selected-event__date']}>
          {formattedDate} <span>{formattedDay}</span>
        </div>
        <div className={styles['selected-event__event-times']}>
          {events
            .filter(item => item?.artistId == artist?.artistId)
            .map(data => {
              const formattedTime = dayjs(data.dateTime, DATE_FORMAT.DATE_TIME).format('h:mm A')
              // If the time has sphere flags, add an asterisk to indicate
              // special conditions
              const titleWithMarker =
                data.sphereFlags && data.sphereFlags.length > 0 ? `${formattedTime}*` : formattedTime
              return (
                <div key={data.id} className={styles['event-time-btn-wrapper']}>
                  <Button
                    key={data.id}
                    color='light'
                    fill={activeShowId === data.id ? 'solid' : 'hollow'}
                    size='sm'
                    title={titleWithMarker}
                    onClick={() => {
                      onTimeSelect(data.id)
                      setActiveEvent(data)
                    }}
                    analytics={{ skipEvent: true }}
                    isFullWidth
                  />
                </div>
              )
            })}
        </div>
        <div className={styles['selected-event__sphere-flags']}>
          {flags.map(flag => (
            // If there are any sphere flags, display them below the event times, but only once
            <Paragraph key={flag} text={{ html: `<p>*${flag}</p>` }} />
          ))}
        </div>
      </div>
      <div className={styles['selected-event__cta-section']}>
        <div className={styles['button-group-wrapper']}>
          <ButtonGroup
            buttons={buttonData as ButtonProps[]}
            layout={isBelowMd ? 'stacked' : 'split'}
            analytics={{
              linkModule: linkModule,
              parentHeadline: artist?.artistName ?? ''
            }}
          />
        </div>

        {artist?.shouldOfferGroupTickets && (
          <div className={styles['selected-event__group-tickets-offer']}>
            {groupTicketsOfferText?.html && <Paragraph text={groupTicketsOfferText} justify='center' noMarginBottom />}
          </div>
        )}
        {disclaimer?.html && (
          <div className={styles['disclaimer']}>
            <Paragraph fontSize='xs' justify='center' text={disclaimer} noMarginBottom />
          </div>
        )}
      </div>
    </div>
  )
}

export default SelectedDay
